import React from 'react';
import { LayoutGrid, List, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ViewMode = 'grid' | 'list';
export type ItemsPerPage = 12 | 24 | 36 | 48;

interface ViewControlsProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  itemsPerPage: ItemsPerPage;
  onItemsPerPageChange: (items: ItemsPerPage) => void;
  totalItems?: number;
  className?: string;
}

const ViewControls: React.FC<ViewControlsProps> = ({
  viewMode,
  onViewModeChange,
  itemsPerPage,
  onItemsPerPageChange,
  totalItems,
  className = ''
}) => {
  const { t } = useTranslation();

  const itemsPerPageOptions: ItemsPerPage[] = [12, 24, 36, 48];

  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      {/* Total items display */}
      {totalItems !== undefined && (
        <div className="text-sm text-gray-600 font-medium">
          <span className="text-gray-900 font-bold">{totalItems}</span> {t('players.playersFound', { defaultValue: 'jugadores encontrados' })}
        </div>
      )}

      <div className="flex items-center gap-3 ml-auto">
        {/* Items per page selector */}
        <div className="relative">
          <label htmlFor="items-per-page" className="text-sm text-gray-700 font-medium mr-2">
            {t('players.itemsPerPage', { defaultValue: 'Mostrar' })}:
          </label>
          <div className="inline-flex items-center">
            <select
              id="items-per-page"
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value) as ItemsPerPage)}
              className="appearance-none bg-white border-2 border-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-lg px-4 py-2 pr-10 text-sm font-semibold text-gray-900 cursor-pointer transition-all duration-200"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-600 pointer-events-none absolute right-3" style={{ marginLeft: '-2rem' }} />
          </div>
        </div>

        {/* View mode toggle */}
        <div className="flex items-center bg-gray-100 rounded-lg p-1 shadow-inner">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
              viewMode === 'grid'
                ? 'bg-white text-blue-600 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title={t('players.gridView', { defaultValue: 'Vista en cuadrícula' })}
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">{t('players.grid', { defaultValue: 'Cuadrícula' })}</span>
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
              viewMode === 'list'
                ? 'bg-white text-blue-600 shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            title={t('players.listView', { defaultValue: 'Vista en lista' })}
          >
            <List className="w-4 h-4" />
            <span className="hidden sm:inline">{t('players.list', { defaultValue: 'Lista' })}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewControls;
